import React, { useState, useEffect } from 'react';
import './QuestionForm.css';

const QuestionForm = ({ question, onSave, onCancel }) => {
  const [questionData, setQuestionData] = useState({
    questionText: '',
    questionType: 'single',
    choices: [
      { id: 'a', text: '', isCorrect: false },
      { id: 'b', text: '', isCorrect: false },
      { id: 'c', text: '', isCorrect: false },
      { id: 'd', text: '', isCorrect: false }
    ],
    explanation: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (question) {
      setQuestionData(question);
    }
  }, [question]);

  const handleQuestionChange = (field, value) => {
    setQuestionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChoiceChange = (index, field, value) => {
    setQuestionData(prev => ({
      ...prev,
      choices: prev.choices.map((choice, i) => 
        i === index ? { ...choice, [field]: value } : choice
      )
    }));
  };

  const handleCorrectAnswerChange = (index, checked) => {
    setQuestionData(prev => ({
      ...prev,
      choices: prev.choices.map((choice, i) => {
        if (prev.questionType === 'single') {
          // Single choice: only one can be correct
          return { ...choice, isCorrect: i === index ? checked : false };
        } else {
          // Multiple choice: multiple can be correct
          return i === index ? { ...choice, isCorrect: checked } : choice;
        }
      })
    }));
  };

  const addChoice = () => {
    const nextLetter = String.fromCharCode(97 + questionData.choices.length); // a, b, c, d, e, ...
    setQuestionData(prev => ({
      ...prev,
      choices: [...prev.choices, { id: nextLetter, text: '', isCorrect: false }]
    }));
  };

  const removeChoice = (index) => {
    if (questionData.choices.length > 2) {
      setQuestionData(prev => ({
        ...prev,
        choices: prev.choices.filter((_, i) => i !== index)
      }));
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !questionData.tags.includes(tagInput.trim())) {
      setQuestionData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setQuestionData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!questionData.questionText.trim()) {
      alert('質問文を入力してください');
      return;
    }

    const filledChoices = questionData.choices.filter(choice => choice.text.trim());
    if (filledChoices.length < 2) {
      alert('最低2つの選択肢を入力してください');
      return;
    }

    const correctChoices = filledChoices.filter(choice => choice.isCorrect);
    if (correctChoices.length === 0) {
      alert('正解を最低1つ選択してください');
      return;
    }

    const finalQuestionData = {
      ...questionData,
      choices: filledChoices
    };

    onSave(finalQuestionData);
  };

  return (
    <div className="question-form">
      <div className="question-form-header">
        <h2>{question ? '質問を編集' : '新しい質問を作成'}</h2>
        <button onClick={onCancel} className="cancel-btn">
          ← 戻る
        </button>
      </div>

      <form onSubmit={handleSubmit} className="question-details-form">
        <div className="form-group">
          <label>質問文 *</label>
          <textarea
            value={questionData.questionText}
            onChange={(e) => handleQuestionChange('questionText', e.target.value)}
            placeholder="質問を入力してください"
            rows="3"
            required
          />
        </div>

        <div className="form-group">
          <label>回答タイプ</label>
          <select
            value={questionData.questionType}
            onChange={(e) => handleQuestionChange('questionType', e.target.value)}
          >
            <option value="single">単一選択</option>
            <option value="multiple">複数選択</option>
          </select>
        </div>

        <div className="form-group">
          <div className="choices-header">
            <label>選択肢 *</label>
            <button 
              type="button" 
              onClick={addChoice}
              className="add-choice-btn"
              disabled={questionData.choices.length >= 8}
            >
              + 選択肢を追加
            </button>
          </div>
          
          <div className="choices-list">
            {questionData.choices.map((choice, index) => (
              <div key={index} className="choice-item">
                <div className="choice-header">
                  <span className="choice-label">{choice.id.toUpperCase()}</span>
                  <input
                    type={questionData.questionType === 'single' ? 'radio' : 'checkbox'}
                    name="correctAnswer"
                    checked={choice.isCorrect}
                    onChange={(e) => handleCorrectAnswerChange(index, e.target.checked)}
                  />
                  <label>正解</label>
                  {questionData.choices.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeChoice(index)}
                      className="remove-choice-btn"
                    >
                      削除
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={choice.text}
                  onChange={(e) => handleChoiceChange(index, 'text', e.target.value)}
                  placeholder={`選択肢${choice.id.toUpperCase()}を入力`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>解説</label>
          <textarea
            value={questionData.explanation}
            onChange={(e) => handleQuestionChange('explanation', e.target.value)}
            placeholder="回答後に表示される解説を入力（任意）"
            rows="3"
          />
        </div>

        <div className="form-group">
          <label>タグ</label>
          <div className="tag-input-container">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="タグを入力してEnterで追加"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
            />
            <button type="button" onClick={addTag} className="add-tag-btn">
              追加
            </button>
          </div>
          
          {questionData.tags.length > 0 && (
            <div className="tags-list">
              {questionData.tags.map((tag, index) => (
                <span key={index} className="tag">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="remove-tag-btn"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="cancel-btn">
            キャンセル
          </button>
          <button type="submit" className="save-btn">
            {question ? '質問を更新' : '質問を保存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default QuestionForm;